import { useEffect } from "react";

const SCRIPT_ID = "Freshdesk-js-sdk";

interface FreshdeskUser {
  id: string;
  email: string;
  fullName: string;
  organization: { id: string; name: string } | null;
}

/**
 * useFreshdeskWidget — injects the Freshdesk Web Chat widget on mount and
 * identifies the current user so chat history is scoped per user / org.
 *
 * Requires three env vars: VITE_FRESHDESK_TOKEN, VITE_FRESHDESK_HOST,
 * VITE_FRESHDESK_WIDGET_ID. If any are missing the hook is a no-op.
 *
 * Call once from a persistent layout component (e.g. PortalLayout), passing
 * the authenticated user. When the user changes the widget is destroyed and
 * re-initialized with the new identity.
 */
export function useFreshdeskWidget(user: FreshdeskUser | null): void {
  useEffect(() => {
    const token    = import.meta.env.VITE_FRESHDESK_TOKEN;
    const host     = import.meta.env.VITE_FRESHDESK_HOST;
    const widgetId = import.meta.env.VITE_FRESHDESK_WIDGET_ID;

    if (!token || !host || !widgetId) return;

    const identify = () => {
      if (!user) return;
      (window as any).fdWidget?.setUser?.({
        externalId: user.id,
        email: user.email,
        name: user.fullName,
        properties: {
          organizationId:   user.organization?.id   ?? "",
          organizationName: user.organization?.name ?? "",
        },
      });
    };

    const initFreshdesk = () => {
      (window as any).fdWidget?.init({ token, host, widgetId });
      identify();
    };

    if (document.getElementById(SCRIPT_ID)) {
      initFreshdesk();
      return;
    }

    const script = document.createElement("script");
    script.id    = SCRIPT_ID;
    script.async = true;
    script.src   = `${host}/webchat/js/widget.js`;
    script.onload = initFreshdesk;
    document.head.appendChild(script);

    return () => {
      document.getElementById(SCRIPT_ID)?.remove();
      try { (window as any).fdWidget?.destroy?.(); } catch (_) {}
    };
  }, [user?.id]); // re-run when the logged-in user changes
}
